import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import OpenWithOutlinedIcon from '@mui/icons-material/OpenWithOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined'
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined'
import SelectAllOutlinedIcon from '@mui/icons-material/SelectAllOutlined'
import MonumentAssemblyEditor from '../FenceModel/ModelViewer/MonumentAssemblyEditor'
import {
    createMonumentAccessorySceneId,
    getMonumentAccessoryParentId,
    normalizeMonumentAssembly,
} from '../FenceModel/ModelViewer/monumentAssembly'
import SceneGuestEditPanel from './SceneGuestEditPanel'
import DeferredNumericInput from './DeferredNumericInput'
import {
    mirrorSceneTargets,
    sceneHistoryJump,
    sceneHistoryRedo,
    sceneHistoryUndo,
    setPositions,
    setRotations,
    setScales,
    setMonumentAssemblies,
    setSelectedModel,
    setSelectedModelPaths,
    setTransformMode,
    toggleHiddenModel,
    toggleMultiSelectMode,
} from '../../actions/sceneActions'

const getBaseId = (value) => {
    const id = String(value || '')
    return id.includes('__') ? id.split('__')[0] : id
}

const getDuplicateIndex = (value) => {
    const id = String(value || '')
    if (!id.includes('__')) return 0

    const index = Number(id.split('__')[1])
    return Number.isFinite(index) ? index : 0
}

const uniq = (items) => [...new Set((items || []).filter(Boolean))]

const toArr3 = (value, fallback = [0, 0, 0]) => {
    if (Array.isArray(value)) {
        return [
            Number(value[0]) || 0,
            Number(value[1]) || 0,
            Number(value[2]) || 0,
        ]
    }

    return [...fallback]
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || min))

const normalizeBaseDimensions = (value) => {
    if (!value || typeof value !== 'object') return null

    const x = Number(value.x)
    const y = Number(value.y)
    const z = Number(value.z)

    if (![x, y, z].every((item) => Number.isFinite(item) && item > 0)) {
        return null
    }

    return { x, y, z }
}

const getScaleIndex = (axis) => (axis === 'x' ? 0 : axis === 'y' ? 1 : 2)

const formatHistoryTime = (value) => {
    if (!value) return ''

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
}

const isTypingTarget = (event) => {
    const target = event?.target
    const tagName = String(target?.tagName || '').toLowerCase()

    return tagName === 'input' ||
        tagName === 'textarea' ||
        tagName === 'select' ||
        !!target?.isContentEditable
}

const SHORTCUT_KEY_ALIASES = {
    c: ['c', 'с'],
    v: ['v', 'м'],
}

const isShortcutKey = (event, latinKey, code) => {
    const eventCode = String(event?.code || '').toLowerCase()
    const eventKey = String(event?.key || '').toLowerCase()
    const aliases = SHORTCUT_KEY_ALIASES[latinKey] || [latinKey]

    return eventCode === String(code || '').toLowerCase() || aliases.includes(eventKey)
}


const TRANSFORM_VIEW_INTERVAL_MS = 34

const vectorsEqual = (left = [], right = []) => (
    left.length === right.length && left.every((value, index) => value === right[index])
)

const transformSnapshotsEqual = (left, right) => !!(
    left &&
    right &&
    vectorsEqual(left.position, right.position) &&
    vectorsEqual(left.rotation, right.rotation) &&
    vectorsEqual(left.scale, right.scale)
)

const readActiveTransform = (store, activeId) => {
    const scene = store.getState()?.scene || {}

    if (!activeId) {
        return {
            position: [0, 0, 0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
        }
    }

    return {
        position: toArr3(scene.positions?.[activeId], [0, 0, 0]),
        rotation: toArr3(scene.rotations?.[activeId], [0, 0, 0]),
        scale: toArr3(scene.scales?.[activeId], [1, 1, 1]),
    }
}

const useActiveTransformView = (store, activeId) => {
    const [snapshot, setSnapshot] = useState(() => readActiveTransform(store, activeId))
    const frameRef = useRef(null)
    const timerRef = useRef(null)
    const lastSyncAtRef = useRef(0)

    useEffect(() => {
        let cancelled = false

        const clearScheduled = () => {
            if (frameRef.current !== null && typeof window !== 'undefined') {
                window.cancelAnimationFrame(frameRef.current)
                frameRef.current = null
            }

            if (timerRef.current !== null && typeof window !== 'undefined') {
                window.clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }

        const sync = () => {
            frameRef.current = null
            timerRef.current = null

            if (cancelled) return

            lastSyncAtRef.current = Date.now()
            const next = readActiveTransform(store, activeId)
            setSnapshot((previous) => (
                transformSnapshotsEqual(previous, next) ? previous : next
            ))
        }

        const schedule = () => {
            if (cancelled || typeof window === 'undefined') return
            if (frameRef.current !== null || timerRef.current !== null) return

            const elapsed = Date.now() - lastSyncAtRef.current
            const wait = Math.max(0, TRANSFORM_VIEW_INTERVAL_MS - elapsed)

            if (wait > 0) {
                timerRef.current = window.setTimeout(() => {
                    timerRef.current = null
                    frameRef.current = window.requestAnimationFrame(sync)
                }, wait)
                return
            }

            frameRef.current = window.requestAnimationFrame(sync)
        }

        sync()
        const unsubscribe = store.subscribe(schedule)

        return () => {
            cancelled = true
            unsubscribe()
            clearScheduled()
        }
    }, [activeId, store])

    return snapshot
}

const SceneGuestToolsDock = ({
    open,
    onClose,
    permissions = {},
    limits = {},
    embedded = false,
    activeTab = '',
    onTabChange,
    products = [],
}) => {
    const dispatch = useDispatch()
    const store = useStore()
    const scene = useSelector((state) => ({
        selectedModel: state.scene?.selectedModel || null,
        selectedModelPaths: state.scene?.selectedModelPaths || [],
        modelPaths: state.scene?.modelPaths || [],
        customObjects: state.scene?.customObjects || {},
        productsMetaById: state.scene?.productsMetaById || {},
        dimensions: state.scene?.dimensions || {},
        hiddenModels: state.scene?.hiddenModels || {},
        transformMode: state.scene?.transformMode || null,
        multiSelectMode: !!state.scene?.multiSelectMode,
        history: state.scene?.history || { past: [], future: [] },
        monumentAssemblies: state.scene?.monumentAssemblies || {},
    }), shallowEqual)

    const canTransform = !!(
        permissions.moveObjects ||
        permissions.rotateObjects ||
        permissions.scaleObjects ||
        permissions.useMirrorModifier
    )
    const canModify = !!(
        permissions.changeQuantity ||
        permissions.useArrayModifier ||
        permissions.useMirrorModifier ||
        permissions.monumentAssembly
    )

    const firstTab = canTransform ? 'transform' : canModify ? 'modifiers' : 'objects'
    const [tab, setTab] = useState(activeTab || firstTab)
    const [message, setMessage] = useState('')
    const [clipboard, setClipboard] = useState(null)

    const selectTab = useCallback((nextTab) => {
        setTab(nextTab)
        onTabChange?.(nextTab)
    }, [onTabChange])

    useEffect(() => {
        if (activeTab && activeTab !== tab) {
            setTab(activeTab)
        }
    }, [activeTab, tab])

    useEffect(() => {
        if (tab === 'transform' && !canTransform) {
            selectTab(canModify ? 'modifiers' : 'objects')
        }

        if (tab === 'modifiers' && !canModify) {
            selectTab(canTransform ? 'transform' : 'objects')
        }
    }, [canModify, canTransform, selectTab, tab])

    const targets = useMemo(() => {
        const selected = Array.isArray(scene.selectedModelPaths)
            ? scene.selectedModelPaths.filter(Boolean)
            : []

        if (selected.length) return uniq(selected)
        return scene.selectedModel ? [scene.selectedModel] : []
    }, [scene.selectedModel, scene.selectedModelPaths])

    const activeId = scene.selectedModel || targets[targets.length - 1] || ''
    const monumentParentId = getMonumentAccessoryParentId(activeId) || activeId
    const activeBaseId = getBaseId(activeId)
    const monumentBaseId = getBaseId(monumentParentId)
    const guestProductsById = useMemo(() => {
        const result = {}

        ;(Array.isArray(products) ? products : []).forEach((product) => {
            const id = String(product?._id || product?.id || '').trim()
            if (id) result[id] = product
        })

        return result
    }, [products])
    const activeModelData = useSelector(
        (state) => state.office?.modelsData?.[activeBaseId] || null,
        shallowEqual
    )
    const monumentModelData = useSelector(
        (state) => state.office?.modelsData?.[monumentBaseId] || null,
        shallowEqual
    )
    const activeTransform = useActiveTransformView(store, activeId)
    const activePosition = activeTransform.position
    const activeRotation = activeTransform.rotation
    const activeScale = activeTransform.scale
    const minScale = Math.max(0.01, Number(limits.minScale || 0.05))
    const maxScale = Math.max(minScale, Number(limits.maxScale || 10))

    const baseDimensionsActive = useMemo(() => {
        if (!activeId) return { x: 1, y: 1, z: 1 }

        const baseId = getBaseId(activeId)
        const customDimensions = normalizeBaseDimensions(
            scene.customObjects?.[activeId]?.baseDimensions
        )

        if (customDimensions) return customDimensions

        const modelDimensions = normalizeBaseDimensions(
            activeModelData?.baseDimensions
        )

        if (modelDimensions) return modelDimensions

        return normalizeBaseDimensions(scene.dimensions?.[activeId]) ||
            normalizeBaseDimensions(scene.dimensions?.[baseId]) ||
            { x: 1, y: 1, z: 1 }
    }, [
        activeId,
        scene.customObjects,
        scene.dimensions,
        activeModelData,
    ])

    const activeFileType = String(
        activeModelData?.fileType ||
        scene.customObjects?.[activeId]?.fileType ||
        ''
    ).toLowerCase()
    const swapVerticalDepthAxes = ['obj', 'glb', 'gltf'].includes(activeFileType)

    const getEffectiveBaseDimension = useCallback((axis) => {
        if (!swapVerticalDepthAxes) return Number(baseDimensionsActive?.[axis]) || 1
        if (axis === 'y') return Number(baseDimensionsActive?.z) || 1
        if (axis === 'z') return Number(baseDimensionsActive?.y) || 1
        return Number(baseDimensionsActive?.x) || 1
    }, [baseDimensionsActive, swapVerticalDepthAxes])

    const dimensionsMM = useMemo(() => (['x', 'y', 'z'].map((axis) => {
        const index = getScaleIndex(axis)
        const baseDimension = getEffectiveBaseDimension(axis)
        const scale = Math.abs(Number(activeScale[index]) || 1)

        return Math.max(0.001, baseDimension * scale * 1000)
    })), [activeScale, getEffectiveBaseDimension])

    const monumentBaseDimensions = useMemo(() => {
        if (!monumentParentId) return { x: 1, y: 1, z: 1 }

        const customDimensions = normalizeBaseDimensions(
            scene.customObjects?.[monumentParentId]?.baseDimensions
        )
        if (customDimensions) return customDimensions

        const modelDimensions = normalizeBaseDimensions(monumentModelData?.baseDimensions)
        if (modelDimensions) return modelDimensions

        return normalizeBaseDimensions(scene.dimensions?.[monumentParentId]) ||
            normalizeBaseDimensions(scene.dimensions?.[monumentBaseId]) ||
            { x: 1, y: 1, z: 1 }
    }, [
        monumentBaseId,
        monumentModelData,
        monumentParentId,
        scene.customObjects,
        scene.dimensions,
    ])

    const monumentScale = useMemo(() => (
        toArr3(scene.scales?.[monumentParentId], [1, 1, 1])
    ), [monumentParentId, scene.scales])

    const monumentFileType = String(
        monumentModelData?.fileType ||
        scene.customObjects?.[monumentParentId]?.fileType ||
        ''
    ).toLowerCase()
    const monumentSwapAxes = ['obj', 'glb', 'gltf'].includes(monumentFileType)
    const monumentDimensions = useMemo(() => {
        const width = Math.max(1, (Number(monumentBaseDimensions.x) || 1) * Math.abs(Number(monumentScale[0]) || 1) * 1000)
        const heightBase = monumentSwapAxes ? monumentBaseDimensions.z : monumentBaseDimensions.y
        const thicknessBase = monumentSwapAxes ? monumentBaseDimensions.y : monumentBaseDimensions.z

        return {
            width,
            height: Math.max(1, (Number(heightBase) || 1) * Math.abs(Number(monumentScale[1]) || 1) * 1000),
            thickness: Math.max(1, (Number(thicknessBase) || 1) * Math.abs(Number(monumentScale[2]) || 1) * 1000),
        }
    }, [monumentBaseDimensions, monumentScale, monumentSwapAxes])

    const monumentProduct = guestProductsById[monumentBaseId] ||
        scene.productsMetaById?.[monumentBaseId] ||
        null
    const monumentAssemblyExisting = scene.monumentAssemblies?.[monumentParentId] || null
    const monumentAssemblyAllowed = !!(
        permissions.monumentAssembly &&
        monumentParentId &&
        !scene.customObjects?.[monumentParentId] &&
        (
            monumentAssemblyExisting ||
            monumentProduct?.monumentAssemblyEnabled === true ||
            monumentProduct?.category?.surfaceEditorEnabled === true ||
            monumentProduct?.surfaceEditorEnabled === true
        )
    )
    const monumentAssemblyValue = monumentAssemblyAllowed
        ? normalizeMonumentAssembly(monumentAssemblyExisting || {}, monumentDimensions)
        : null

    const handleMonumentAssemblyChange = (assembly) => {
        if (!monumentAssemblyAllowed || !monumentParentId) return

        dispatch(setMonumentAssemblies({
            [monumentParentId]: normalizeMonumentAssembly(assembly || {}, monumentDimensions),
        }))
    }

    const handleMonumentAccessorySelect = (target = {}) => {
        if (!monumentParentId) return

        const role = String(target?.role || '').trim()
        if (!role) return

        const accessoryId = createMonumentAccessorySceneId(
            monumentParentId,
            role,
            Math.max(1, Number(target?.quantityIndex) || 1)
        )

        dispatch(setSelectedModel(accessoryId))
        dispatch(setSelectedModelPaths([accessoryId]))
    }

    const objectPaths = useMemo(() => uniq([
        ...(Array.isArray(scene.modelPaths) ? scene.modelPaths : []),
        ...Object.keys(scene.customObjects || {}),
    ]), [scene.customObjects, scene.modelPaths])

    const getObjectName = useCallback((path) => {
        const custom = scene.customObjects?.[path]
        if (custom?.name) return custom.name

        const baseId = getBaseId(path)
        const meta = scene.productsMetaById?.[baseId] || {}
        const baseName = meta.fullName || meta.name || (meta.article ? `Арт. ${meta.article}` : '') || baseId
        const duplicateIndex = getDuplicateIndex(path)

        return duplicateIndex > 0
            ? `${baseName} — копия ${duplicateIndex}`
            : baseName
    }, [scene.customObjects, scene.productsMetaById])

    const activeName = activeId ? getObjectName(activeId) : 'Объект не выбран'

    const selectObject = (path, event) => {
        const additive = !!(event?.shiftKey || scene.multiSelectMode)

        if (!additive) {
            dispatch(setSelectedModel(path))
            dispatch(setSelectedModelPaths([path]))
            return
        }

        const current = targets
        const exists = current.includes(path)
        const next = exists
            ? current.filter((item) => item !== path)
            : uniq([...current, path])
        const nextActive = exists
            ? (next[next.length - 1] || null)
            : path

        dispatch(setSelectedModel(nextActive))
        dispatch(setSelectedModelPaths(next))
    }

    const setMode = (mode) => {
        if (mode === 'translate' && !permissions.moveObjects) return
        if (mode === 'rotate' && !permissions.rotateObjects) return
        if (mode === 'scale' && !permissions.scaleObjects) return

        dispatch(setTransformMode(scene.transformMode === mode ? null : mode))
    }

    const applyVectorValue = (kind, axis, rawValue) => {
        if (!targets.length) return

        const index = axis === 'x' ? 0 : axis === 'y' ? 1 : 2
        const number = Number(String(rawValue).replace(',', '.'))

        if (!Number.isFinite(number)) return

        if (kind === 'position' && !permissions.moveObjects) return
        if (kind === 'rotation' && !permissions.rotateObjects) return
        if (kind === 'scale' && !permissions.scaleObjects) return

        const next = {}
        const currentScene = store.getState()?.scene || {}

        targets.forEach((path) => {
            const source = kind === 'position'
                ? toArr3(currentScene.positions?.[path], [0, 0, 0])
                : kind === 'rotation'
                    ? toArr3(currentScene.rotations?.[path], [0, 0, 0])
                    : toArr3(currentScene.scales?.[path], [1, 1, 1])
            const value = [...source]

            if (kind === 'rotation') {
                value[index] = number * Math.PI / 180
            } else if (kind === 'scale') {
                const sign = Number(source[index]) < 0 ? -1 : 1
                value[index] = sign * clamp(Math.abs(number), minScale, maxScale)
            } else {
                value[index] = number
            }

            next[path] = value
        })

        if (kind === 'position') dispatch(setPositions(next))
        if (kind === 'rotation') dispatch(setRotations(next))
        if (kind === 'scale') dispatch(setScales(next))
    }

    const stepVector = (kind, axis, delta) => {
        const index = axis === 'x' ? 0 : axis === 'y' ? 1 : 2
        const source = kind === 'position'
            ? activePosition
            : kind === 'rotation'
                ? activeRotation.map((value) => value * 180 / Math.PI)
                : activeScale.map((value) => Math.abs(value))

        applyVectorValue(kind, axis, Number(source[index] || 0) + delta)
    }

    const applyDimensionMMValue = (axis, rawValue) => {
        if (!permissions.scaleObjects || !activeId || !targets.length) return

        const requestedMM = Number(String(rawValue).replace(',', '.'))

        if (!Number.isFinite(requestedMM) || requestedMM <= 0) return

        const index = getScaleIndex(axis)
        const currentMM = Math.max(0.001, Number(dimensionsMM[index]) || 0.001)
        const ratio = requestedMM / currentMM
        const next = {}
        const currentScene = store.getState()?.scene || {}

        targets.forEach((path) => {
            const source = toArr3(currentScene.scales?.[path], [1, 1, 1])
            const value = [...source]
            const sign = Number(source[index]) < 0 ? -1 : 1
            const currentScale = Math.abs(Number(source[index]) || 1)

            value[index] = sign * clamp(currentScale * ratio, minScale, maxScale)
            next[path] = value
        })

        dispatch(setScales(next))
    }

    const stepDimensionMM = (axis, deltaMM) => {
        const index = getScaleIndex(axis)
        const currentMM = Number(dimensionsMM[index]) || 0

        applyDimensionMMValue(axis, Math.max(1, currentMM + deltaMM))
    }

    const copyTransform = useCallback(() => {
        if (!activeId) return

        setClipboard({
            position: [...activePosition],
            rotation: [...activeRotation],
            scale: [...activeScale],
        })
        setMessage('Параметры объекта скопированы')
    }, [activeId, activePosition, activeRotation, activeScale])

    const pasteTransform = useCallback(() => {
        if (!clipboard || !targets.length) return

        const positions = {}
        const rotations = {}
        const scales = {}

        targets.forEach((path) => {
            if (permissions.moveObjects) positions[path] = [...clipboard.position]
            if (permissions.rotateObjects) rotations[path] = [...clipboard.rotation]
            if (permissions.scaleObjects) scales[path] = [...clipboard.scale]
        })

        if (Object.keys(positions).length) dispatch(setPositions(positions))
        if (Object.keys(rotations).length) dispatch(setRotations(rotations))
        if (Object.keys(scales).length) dispatch(setScales(scales))

        setMessage('Разрешённые параметры вставлены')
    }, [clipboard, dispatch, permissions.moveObjects, permissions.rotateObjects, permissions.scaleObjects, targets])

    useEffect(() => {
        if (!open || typeof window === 'undefined') return undefined

        const handleClipboardHotkeys = (event) => {
            if (isTypingTarget(event)) return

            const ctrl = !!(event.ctrlKey || event.metaKey)
            if (!ctrl || event.altKey) return

            if (isShortcutKey(event, 'c', 'KeyC') && activeId) {
                event.preventDefault()
                copyTransform()
                return
            }

            if (isShortcutKey(event, 'v', 'KeyV') && clipboard && targets.length) {
                event.preventDefault()
                pasteTransform()
            }
        }

        window.addEventListener('keydown', handleClipboardHotkeys)

        return () => {
            window.removeEventListener('keydown', handleClipboardHotkeys)
        }
    }, [activeId, clipboard, copyTransform, open, pasteTransform, targets.length])

    const mirror = (axis) => {
        if (!permissions.useMirrorModifier || !targets.length) return

        const result = dispatch(mirrorSceneTargets(targets, axis))
        setMessage(result?.ok ? `Зеркало ${axis.toUpperCase()} применено` : (result?.error || 'Не удалось применить зеркало'))
    }

    const historyPast = Array.isArray(scene.history?.past) ? scene.history.past : []
    const historyFuture = Array.isArray(scene.history?.future) ? scene.history.future : []
    const historyTimeline = useMemo(
        () => [...historyPast, ...historyFuture],
        [historyFuture, historyPast]
    )
    const historyIndex = Math.max(0, historyPast.length - 1)
    const canUndo = historyPast.length > 1
    const canRedo = historyFuture.length > 0

    const renderAxisFields = (kind) => {
        const values = kind === 'position'
            ? activePosition
            : kind === 'rotation'
                ? activeRotation.map((value) => value * 180 / Math.PI)
                : activeScale.map((value) => Math.abs(value))
        const step = kind === 'rotation' ? 1 : 0.1
        const precision = kind === 'rotation' ? 0 : 2

        return (
            <div className="sceneGuestToolsDock__axisGrid">
                {['x', 'y', 'z'].map((axis, index) => (
                    <label key={axis}>
                        <span>{axis.toUpperCase()}</span>
                        <div>
                            <DeferredNumericInput
                                value={values[index]}
                                precision={precision}
                                min={kind === 'scale' ? minScale : undefined}
                                max={kind === 'scale' ? maxScale : undefined}
                                allowNegative={kind !== 'scale'}
                                disabled={!activeId}
                                ariaLabel={`${kind} ${axis.toUpperCase()}`}
                                onCommit={(value) => applyVectorValue(kind, axis, value)}
                            />
                            <span className="sceneGuestToolsDock__stepper">
                                <button type="button" onClick={() => stepVector(kind, axis, step)} disabled={!activeId}>+</button>
                                <button type="button" onClick={() => stepVector(kind, axis, -step)} disabled={!activeId}>−</button>
                            </span>
                        </div>
                    </label>
                ))}
            </div>
        )
    }

    const renderDimensionFields = () => (
        <div className="sceneGuestToolsDock__dimensionBlock">
            <div className="sceneGuestToolsDock__dimensionTitle">
                <strong>Размер объекта</strong>
                <span>мм</span>
            </div>
            <div className="sceneGuestToolsDock__axisGrid sceneGuestToolsDock__axisGrid--dimensions">
                {['x', 'y', 'z'].map((axis, index) => (
                    <label key={axis}>
                        <span>{axis.toUpperCase()}</span>
                        <div>
                            <DeferredNumericInput
                                value={dimensionsMM[index]}
                                precision={1}
                                min={Math.max(0.001, getEffectiveBaseDimension(axis) * minScale * 1000)}
                                max={Math.max(0.001, getEffectiveBaseDimension(axis) * maxScale * 1000)}
                                allowNegative={false}
                                disabled={!activeId}
                                ariaLabel={`Размер по ${axis.toUpperCase()} в миллиметрах`}
                                onCommit={(value) => applyDimensionMMValue(axis, value)}
                            />
                            <span className="sceneGuestToolsDock__stepper">
                                <button type="button" onClick={() => stepDimensionMM(axis, 10)} disabled={!activeId}>+</button>
                                <button type="button" onClick={() => stepDimensionMM(axis, -10)} disabled={!activeId}>−</button>
                            </span>
                        </div>
                    </label>
                ))}
            </div>
            <small>Размер меняет масштаб выбранного объекта. Шаг кнопок — 10 мм.</small>
        </div>
    )

    const activeKind = scene.transformMode === 'translate'
        ? 'position'
        : scene.transformMode === 'rotate'
            ? 'rotation'
            : scene.transformMode === 'scale'
                ? 'scale'
                : null

    if (!open) return null

    return (
        <aside className={`sceneGuestToolsDock ${embedded ? 'is-embedded' : ''}`} aria-label="Инструменты гостевой сцены">
            <nav className="sceneGuestToolsDock__rail">
                {canTransform ? (
                    <button
                        type="button"
                        className={tab === 'transform' ? 'is-active' : ''}
                        onClick={() => selectTab('transform')}
                        title="Трансформация"
                    >
                        <OpenWithOutlinedIcon />
                    </button>
                ) : null}
                <button
                    type="button"
                    className={tab === 'objects' ? 'is-active' : ''}
                    onClick={() => selectTab('objects')}
                    title="Список объектов"
                >
                    <ViewListOutlinedIcon />
                </button>
                {canModify ? (
                    <button
                        type="button"
                        className={tab === 'modifiers' ? 'is-active' : ''}
                        onClick={() => selectTab('modifiers')}
                        title="Модификаторы"
                    >
                        <AutoAwesomeMotionOutlinedIcon />
                    </button>
                ) : null}
                <button
                    type="button"
                    className={tab === 'history' ? 'is-active' : ''}
                    onClick={() => selectTab('history')}
                    title="История"
                >
                    <HistoryOutlinedIcon />
                </button>
            </nav>

            <section className="sceneGuestToolsDock__panel">
                <header>
                    <div>
                        <span>{tab === 'objects' ? 'Список объектов' : tab === 'modifiers' ? 'Модификаторы' : tab === 'history' ? 'История действий' : 'Управление объектом'}</span>
                        <strong>{tab === 'objects' ? `${objectPaths.length} объектов` : activeName}</strong>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Закрыть инструменты">
                        <CloseOutlinedIcon />
                    </button>
                </header>

                <div className="sceneGuestToolsDock__body">
                    {tab === 'transform' && canTransform ? (
                        <div className="sceneGuestToolsDock__transform">
                            <div className="sceneGuestToolsDock__modeTabs">
                                <button type="button" className={!scene.transformMode ? 'is-active' : ''} onClick={() => dispatch(setTransformMode(null))}>Объект</button>
                                <button type="button" className={scene.transformMode === 'translate' ? 'is-active' : ''} onClick={() => setMode('translate')} disabled={!permissions.moveObjects}>Перем.</button>
                                <button type="button" className={scene.transformMode === 'rotate' ? 'is-active' : ''} onClick={() => setMode('rotate')} disabled={!permissions.rotateObjects}>Повор.</button>
                                <button type="button" className={scene.transformMode === 'scale' ? 'is-active' : ''} onClick={() => setMode('scale')} disabled={!permissions.scaleObjects}>Масшт.</button>
                            </div>

                            {activeKind ? (
                                <>
                                    {activeKind === 'scale' ? renderDimensionFields() : null}
                                    <div className="sceneGuestToolsDock__vectorBlock">
                                        <div className="sceneGuestToolsDock__dimensionTitle">
                                            <strong>
                                                {activeKind === 'position'
                                                    ? 'Положение'
                                                    : activeKind === 'rotation'
                                                        ? 'Поворот'
                                                        : 'Масштаб'}
                                            </strong>
                                            <span>
                                                {activeKind === 'rotation' ? '°' : activeKind === 'position' ? 'м' : 'коэффициент'}
                                            </span>
                                        </div>
                                        {renderAxisFields(activeKind)}
                                    </div>
                                </>
                            ) : (
                                <div className="sceneGuestToolsDock__objectSummary">
                                    <span>Выберите режим изменения или используйте горячие клавиши:</span>
                                    <strong>
                                        {permissions.moveObjects ? 'G — перемещение' : ''}
                                        {permissions.rotateObjects ? ' · R — поворот' : ''}
                                        {permissions.scaleObjects ? ' · S — масштаб' : ''}
                                    </strong>
                                </div>
                            )}

                            <div className="sceneGuestToolsDock__quickGrid">
                                <button type="button" onClick={copyTransform} disabled={!activeId}><ContentCopyOutlinedIcon /> Копия</button>
                                <button type="button" onClick={pasteTransform} disabled={!clipboard || !targets.length}><ContentPasteOutlinedIcon /> Вставка</button>
                            </div>

                            {permissions.useMirrorModifier ? (
                                <div className="sceneGuestToolsDock__mirrorGrid">
                                    <button type="button" onClick={() => mirror('x')} disabled={!targets.length}><FlipOutlinedIcon /> Зерк. X</button>
                                    <button type="button" onClick={() => mirror('y')} disabled={!targets.length}><FlipOutlinedIcon /> Зерк. Y</button>
                                    <button type="button" onClick={() => mirror('z')} disabled={!targets.length}><FlipOutlinedIcon /> Зерк. Z</button>
                                    <button
                                        type="button"
                                        className={scene.multiSelectMode ? 'is-active' : ''}
                                        onClick={() => dispatch(toggleMultiSelectMode())}
                                    >
                                        <SelectAllOutlinedIcon /> Мульти
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className={`sceneGuestToolsDock__multi ${scene.multiSelectMode ? 'is-active' : ''}`}
                                    onClick={() => dispatch(toggleMultiSelectMode())}
                                >
                                    <SelectAllOutlinedIcon /> Мультивыделение
                                </button>
                            )}

                            <div className="sceneGuestToolsDock__historyBar">
                                <button type="button" onClick={() => dispatch(sceneHistoryUndo())} disabled={!canUndo}><UndoOutlinedIcon /></button>
                                <span>{historyPast[historyIndex]?.historyLabel || 'История пуста'}</span>
                                <button type="button" onClick={() => dispatch(sceneHistoryRedo())} disabled={!canRedo}><RedoOutlinedIcon /></button>
                                <b>{historyPast.length}/{historyTimeline.length || 1}</b>
                            </div>

                            <small>Esc — выйти · Ctrl+C / Ctrl+V — копия / вставка · Ctrl+Z — назад · Ctrl+Y / Ctrl+Shift+Z — вперёд</small>
                        </div>
                    ) : null}

                    {tab === 'objects' ? (
                        <div className="sceneGuestToolsDock__objects">
                            {objectPaths.length ? objectPaths.map((path) => {
                                const selected = targets.includes(path)
                                const hidden = !!scene.hiddenModels?.[path]
                                const custom = !!scene.customObjects?.[path]

                                return (
                                    <article key={path} className={selected ? 'is-selected' : ''}>
                                        <button type="button" className="sceneGuestToolsDock__objectMain" onClick={(event) => selectObject(path, event)}>
                                            <strong>{getObjectName(path)}</strong>
                                            <span>{selected ? 'активный' : custom ? 'custom' : getBaseId(path).slice(-8)}</span>
                                        </button>
                                        <button
                                            type="button"
                                            className="sceneGuestToolsDock__visibility"
                                            onClick={() => dispatch(toggleHiddenModel(path))}
                                            title={hidden ? 'Показать объект' : 'Скрыть объект'}
                                        >
                                            {hidden ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                        </button>
                                    </article>
                                )
                            }) : (
                                <div className="sceneGuestToolsDock__empty">В сцене пока нет объектов.</div>
                            )}
                            <small>Shift + клик или кнопка «Мульти» — выбрать несколько объектов.</small>
                        </div>
                    ) : null}

                    {tab === 'modifiers' && canModify ? (
                        <div className="sceneGuestToolsDock__modifiers">
                            {(permissions.changeQuantity || permissions.useArrayModifier || permissions.useMirrorModifier) ? (
                                <SceneGuestEditPanel permissions={permissions} limits={limits} />
                            ) : null}

                            {permissions.monumentAssembly ? (
                                monumentAssemblyAllowed && monumentAssemblyValue ? (
                                    <div className="sceneGuestToolsDock__monumentAssembly">
                                        <MonumentAssemblyEditor
                                            value={monumentAssemblyValue}
                                            monumentDimensions={monumentDimensions}
                                            selectedAccessoryId={activeId}
                                            onChange={handleMonumentAssemblyChange}
                                            onSelectAccessory={handleMonumentAccessorySelect}
                                            compact
                                        />
                                    </div>
                                ) : (
                                    <div className="sceneGuestToolsDock__empty">
                                        Выберите памятник из категории, для которой включён «Комплект памятника».
                                    </div>
                                )
                            ) : null}
                        </div>
                    ) : null}

                    {tab === 'history' ? (
                        <div className="sceneGuestToolsDock__timeline">
                            <div className="sceneGuestToolsDock__historyActions">
                                <button type="button" onClick={() => dispatch(sceneHistoryUndo())} disabled={!canUndo}><UndoOutlinedIcon /> Назад</button>
                                <button type="button" onClick={() => dispatch(sceneHistoryRedo())} disabled={!canRedo}><RedoOutlinedIcon /> Вперёд</button>
                            </div>
                            {historyTimeline.length ? historyTimeline.map((entry, index) => (
                                <button
                                    type="button"
                                    key={entry.historyId || `${entry.historyAt || 'history'}-${index}`}
                                    className={index === historyIndex ? 'is-current' : ''}
                                    onClick={() => dispatch(sceneHistoryJump(index))}
                                >
                                    <span>{index + 1}</span>
                                    <div>
                                        <strong>{entry.historyLabel || `Действие ${index + 1}`}</strong>
                                        <small>{formatHistoryTime(entry.historyAt)}</small>
                                    </div>
                                </button>
                            )) : (
                                <div className="sceneGuestToolsDock__empty">История появится после первого изменения сцены.</div>
                            )}
                        </div>
                    ) : null}

                    {message ? <div className="sceneGuestToolsDock__message">{message}</div> : null}
                </div>
            </section>
        </aside>
    )
}

export default React.memo(SceneGuestToolsDock)
